/**
 * @component CompanyMaintenancePage
 * @description 새힘정보기술의 장기 유지보수 고객사를 소개하는 페이지입니다.
 *              유지보수 기간을 기준으로 계층화된 카드 레이아웃으로 솔루션의 안정성·신뢰성을 강조합니다.
 * @usage App 라우팅에서 `/company/maintenance.html` 경로로 렌더링됩니다.
 */

import PageHero from '../../components/common/PageHero';
import SectionHeading from '../../components/common/SectionHeading';
import { pageMeta } from '../../constants/navigation';

// [비즈니스] 장기 유지보수 고객사 — 솔루션 도입 후 장기간 서비스 중인 고객사 목록으로,
// 안정성·활용성의 객관적 근거로 활용됩니다. 도입연도 오름차순(오래된 순)으로 나열합니다.
// duration 필드를 제거하고 현재 연도 기준으로 자동 계산합니다.
const MAINTENANCE_RECORD = [
  { id: 'm-shinpoong',    name: '신풍제약',                introYear: 2013, system: '제조실행관리시스템(MES)' },
  { id: 'm-cmjc',         name: '씨엠제이씨(구.일동제약)', introYear: 2013, system: '칭량관리시스템(RWS)' },
  { id: 'm-donghwa',      name: '동화약품',                introYear: 2013, system: '칭량관리시스템(RWS)' },
  { id: 'm-genic',        name: '제닉',                    introYear: 2013, system: '칭량관리시스템(RWS)' },
  { id: 'm-daehwa',       name: '대화제약',                introYear: 2013, system: '제조실행관리시스템(MES)' },
  { id: 'm-hanolbio',     name: '한올바이오파마',           introYear: 2014, system: '자재/창고관리시스템(MWS)' },
  { id: 'm-cosmax-us',    name: '코스맥스(미국)',           introYear: 2014, system: '칭량관리시스템(RWS)' },
  { id: 'm-kwangdong',    name: '광동제약',                introYear: 2015, system: '제조실행관리시스템(MES)' },
  { id: 'm-otsuka',       name: '한국오츠카제약_향남',      introYear: 2015, system: '칭량관리시스템(RWS)' },
  { id: 'm-ajupharm',     name: '아주약품',                introYear: 2016, system: '창고관리시스템(WMS)' },
  { id: 'm-cosmax-kr',    name: '코스맥스(한국-향남/평택)', introYear: 2016, system: '창고관리시스템(WMS)' },
  { id: 'm-kuhnil',       name: '건일제약',                introYear: 2019, system: '칭량관리시스템(RWS)' },
  { id: 'm-fmg',          name: '에프엠지',                introYear: 2019, system: '창고관리시스템(WMS) + 제조실행관리시스템(MES)' },
  { id: 'm-cosmaxnbt-au', name: '코스맥스NBT(호주)',        introYear: 2019, system: '창고관리시스템(WMS)' },
  { id: 'm-arlico',       name: '알리코제약',              introYear: 2020, system: '창고관리시스템(WMS)' },
  { id: 'm-firson',       name: '퍼슨제약',                introYear: 2020, system: '창고관리시스템(WMS)' },
  { id: 'm-cosmecca',     name: '코스메카코리아',           introYear: 2020, system: '제조실행관리시스템(MES)' },
  { id: 'm-cosmaxbio',    name: '코스맥스바이오(한국-제천)', introYear: 2020, system: '창고관리시스템(WMS)' },
  { id: 'm-cosmaxnbt-kr', name: '코스맥스NBT(한국-이천)',   introYear: 2020, system: '창고관리시스템(WMS)' },
  { id: 'm-bell-ins',     name: '벨아이앤에스(종근당)',     introYear: 2021, system: '제조실행관리시스템(MES)' },
  { id: 'm-mmpharm',      name: '명문제약',                introYear: 2021, system: '창고관리시스템(WMS)' },
  { id: 'm-daewonpharm',  name: '대원제약',                introYear: 2022, system: '칭량관리시스템(RWS) + 창고관리' },
  { id: 'm-curocell',     name: '큐로셀',                  introYear: 2022, system: '추적관리시스템(Traceability)' },
  { id: 'm-genuone',      name: '제뉴원사이언스',           introYear: 2022, system: '제조실행관리시스템(MES)' },
  { id: 'm-woongjin',     name: '웅진(다림바이오텍)',       introYear: 2023, system: '제조실행관리시스템(MES)' },
  { id: 'm-bukwang',      name: '부광약품',                introYear: 2023, system: '칭량관리시스템(RWS) + 창고관리' },
  { id: 'm-yungjin',      name: '영진약품',                introYear: 2023, system: '제조실행관리시스템(MES)' },
  { id: 'm-cosmax-id',    name: '코스맥스(인도네시아)',     introYear: 2023, system: '칭량관리시스템(RWS)' },
  { id: 'm-englewoodlab', name: '잉글우드랩코리아',         introYear: 2025, system: '제조실행관리시스템(MES)' },
];

const THIS_YEAR = new Date().getFullYear();

// [비즈니스] 유지보수 기간을 현재 연도 기준으로 자동 계산합니다.
// 연도가 지남에 따라 기간이 자동 증가하고, 티어도 자동으로 상위로 이동합니다.
const calcDuration = (introYear) => THIS_YEAR - introYear;

// [비즈니스] 계산된 유지보수 기간 기준으로 티어를 분류합니다.
// 10년 이상 → 골드, 5~9년 → 블루, 4년 이하 → 그린
const getTierColorClass = (introYear) => {
  const years = calcDuration(introYear);
  if (years >= 10) return 'gold';
  if (years >= 5)  return 'blue';
  return 'green';
};

const TIERS = [
  {
    id:         'tier-10plus',
    label:      '10년 이상',
    colorClass: 'gold',
    companies:  MAINTENANCE_RECORD.filter((c) => calcDuration(c.introYear) >= 10),
  },
  {
    id:         'tier-5to9',
    label:      '5 ~ 9년',
    colorClass: 'blue',
    companies:  MAINTENANCE_RECORD.filter((c) => calcDuration(c.introYear) >= 5 && calcDuration(c.introYear) < 10),
  },
  {
    id:         'tier-under5',
    label:      '4년 이하',
    colorClass: 'green',
    companies:  MAINTENANCE_RECORD.filter((c) => calcDuration(c.introYear) < 5),
  },
];

// [비즈니스] KPI 수치도 현재 연도 기준으로 자동 계산합니다.
const maxDuration   = Math.max(...MAINTENANCE_RECORD.map((c) => calcDuration(c.introYear)));
const longTermCount = MAINTENANCE_RECORD.filter((c) => calcDuration(c.introYear) >= 10).length;

const STATS = [
  { value: `${MAINTENANCE_RECORD.length}개사`, label: '장기 유지보수 고객사' },
  { value: `${maxDuration}년`,                 label: '최장 유지보수 기간' },
  { value: `${longTermCount}개사`,             label: '10년 이상 장기 고객사' },
];

export default function CompanyMaintenancePage({ onNavigate }) {
  return (
    <>
      <PageHero
        title={pageMeta.companyMaintenance.title}
        trail={pageMeta.companyMaintenance.trail}
        className="company-page-hero"
      />
      <section className="section">
        <div className="container">
          <SectionHeading
            eyebrow="Long-term Partnership"
            title="검증된 신뢰, 오랜 파트너십"
            description="새힘정보기술은 제약·화장품 분야 선도 기업들과 평균 7년 이상의 유지보수 계약을 유지하고 있습니다."
            className="fade-in"
          />

          {/* [비즈니스] 핵심 수치를 전면 노출해 솔루션의 안정성을 한눈에 인식하게 합니다. */}
          <div className="maint-stats-bar">
            {STATS.map(({ value, label }) => (
              <div key={label} className="maint-stat-item">
                <span className="maint-stat-value">{value}</span>
                <span className="maint-stat-label">{label}</span>
              </div>
            ))}
          </div>

          <p className="maint-intro-desc">
            신풍제약·씨엠제이씨·동화약품 등 {longTermCount}개사가 솔루션 도입 후 
            장기간 유지보수 서비스를 제공받고 있는 다수의 고객사들이 당사 솔루션의 안정성과 활용성 증명합니다.
          </p>

          {/* [비즈니스] 유지보수 기간을 기준으로 티어를 구분해 장기 고객의 충성도를 직관적으로 전달합니다. */}
          <div className="maint-tiers">
            {TIERS.map(({ id, label, colorClass, companies }) => (
              <div key={id} className="maint-tier">
                <div className="maint-tier-header">
                  <span className={`maint-tier-badge maint-tier-badge--${colorClass}`}>{label}</span>
                  <span className="maint-tier-count">{companies.length}개사</span>
                </div>
                <div className="maint-cards">
                  {companies.map(({ id: cid, name, introYear, system }) => (
                    <div key={cid} className="maint-card">
                      <div className="maint-card-body">
                        <span className="maint-card-name">{name}</span>
                        <span className="maint-card-system">{system}</span>
                      </div>
                      <div className="maint-card-meta">
                        <span className="maint-card-intro">{introYear}년 도입</span>
                        <span className={`maint-duration-badge maint-duration-badge--${getTierColorClass(introYear)}`}>
                          {calcDuration(introYear)}년
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
